import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Modal } from '../ui/modal.jsx';
import { api, ApiError } from '../../api/index.js';

/**
 * Stage-3 — Issue terminal enrollment ticket.
 *
 * Flow:
 *   1. Operator opens modal for a person.
 *   2. Picks TTL (default 15 min) → POST /api/persons/:id/enroll-terminal.
 *   3. Backend proxies to mesh microservice's /api/admin/enrollment-tickets.
 *   4. Modal shows QR (containing the qrPayload JSON the mesh ships) +
 *      manual fallback fields (microservice URL, ticketId, secret).
 *   5. Operator either scans on the tablet or types the values into
 *      the TMS Android client by hand.
 *   6. Closing the modal drops the secret from React state.
 *
 * Display lifecycle: state.phase ∈ 'idle' | 'minting' | 'shown' | 'error'.
 *   idle    — TTL form, "Mint" button
 *   minting — spinner
 *   shown   — QR + fields
 *   error   — error banner with retry
 */
/**
 * Issues a one-shot terminal enrollment ticket for a person.
 *
 * Caller (personnel-page) is responsible for the duplicate-binding
 * gate: if the person already has an active terminal, the caller pops
 * its own ConfirmDialog FIRST and only opens this modal once the
 * operator has explicitly approved revoke-and-rebind. In that case
 * `force=true` is threaded through every mint() call so the backend
 * revokes the old terminal atomically before issuing the new ticket.
 *
 * If `force=false` and the backend nevertheless returns 409 (race —
 * another admin enrolled the same person between the gate-check and
 * mint), the modal surfaces a plain error banner with the existing-
 * terminal details. Operator closes and starts over from Personnel.
 */
export function EnrollTerminalModal({ open, person, onClose, force = false }) {
  const [phase, setPhase] = useState('idle');
  const [ttlMinutes, setTtlMinutes] = useState(15);
  // 2026-07-05 (Round 1.450) — cert validity picker (days). Default 365
  // matches coord's CA_TERMINAL_CERT_TTL_DAYS. Operator can override
  // per-enrollment.
  const [validityDays, setValidityDays] = useState(365);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const qrCanvasRef = useRef(null);

  // Reset state when the modal opens or the person changes — we never
  // want a stale secret from a previous mint to flash.
  useEffect(() => {
    if (!open) {
      setPhase('idle');
      setError(null);
      setResult(null);
      return;
    }
  }, [open, person?.id]);

  // Re-render the QR whenever a fresh ticket lands.
  useEffect(() => {
    if (phase !== 'shown' || !result?.qrPayload || !qrCanvasRef.current) return;
    QRCode.toCanvas(
      qrCanvasRef.current,
      JSON.stringify(result.qrPayload),
      { width: 280, margin: 2, errorCorrectionLevel: 'M' },
      (err) => { if (err) console.warn('QR render failed', err); },
    );
  }, [phase, result]);

  async function mint() {
    setPhase('minting');
    setError(null);
    try {
      const r = await api.persons.enrollTerminal(person.id, ttlMinutes, { force, validityDays });
      setResult(r);
      setPhase('shown');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.payload?.existingTerminal) {
        // Race: caller's gate-check said no active terminal, but by
        // the time we got here someone else enrolled. Tell the
        // operator and let them retry from Personnel (where the
        // confirm-dialog gate will catch it on the next click).
        const t = e.payload.existingTerminal;
        const shortUuid = (t.uuid || '').slice(0, 8);
        setError(
          `Another admin enrolled a terminal for this person in parallel `
          + `(${shortUuid}…, enrolled ${t.enrolledAt}). Close this dialog and try again — `
          + `you'll be asked whether to replace the existing binding.`,
        );
        setPhase('error');
        return;
      }
      setError(e?.message || 'Failed to issue ticket');
      setPhase('error');
    }
  }

  function copyToClipboard(value, label) {
    if (!value) return;
    navigator.clipboard?.writeText(value).catch(() => {});
    // Lightweight feedback — just blink the icon. Toast lib could go
    // here later if we want it system-wide.
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Issue terminal — ${person?.displayName || ''}`}
      description={person?.callsign ? `Callsign ${person.callsign}` : null}
      size="lg"
      footer={
        phase === 'shown' ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent/90"
          >
            Done
          </button>
        ) : phase === 'error' ? (
          <>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-line px-4 py-2 text-sm text-mist hover:bg-canvas"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => mint(false)}
              className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent/90"
            >
              Retry
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-line px-4 py-2 text-sm text-mist hover:bg-canvas"
            >
              Cancel
            </button>
            <button
              type="button"
              // Wrap in arrow so React's SyntheticEvent doesn't leak
              // into the first arg — `mint(force = false)` would see
              // the event object, coerce it to truthy, and silently
              // skip the 409 duplicate-check. Audit log proved it:
              // the event came through as force=true, backend went
              // straight to revoke+mint without the dialog. Same
              // gotcha applies on the Retry button above.
              onClick={() => mint(false)}
              disabled={phase === 'minting'}
              className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {phase === 'minting' ? 'Minting…' : 'Mint ticket'}
            </button>
          </>
        )
      }
    >
      {(phase === 'idle' || phase === 'minting') && (
        <div className="space-y-4">
          <p className="text-sm text-ink">
            A one-shot enrollment ticket is generated on the local mesh microservice.
            The terminal scans the QR (or pastes the fields manually) to bind itself
            to this person's identity.
          </p>
          <label className="flex items-center gap-3 text-sm text-ink">
            <span className="w-32">Ticket TTL (min)</span>
            <input
              type="number"
              min={1}
              max={1440}
              value={ttlMinutes}
              onChange={(e) => setTtlMinutes(Number(e.target.value))}
              className="w-24 rounded-lg border border-line bg-white px-3 py-1.5 text-sm focus:border-accent focus:outline-none"
            />
            <span className="text-xs text-mist">ticket expires unused after</span>
          </label>
          <label className="flex items-center gap-3 text-sm text-ink">
            <span className="w-32">Cert validity (days)</span>
            <input
              type="number"
              min={1}
              max={3650}
              value={validityDays}
              onChange={(e) => setValidityDays(Number(e.target.value))}
              className="w-24 rounded-lg border border-line bg-white px-3 py-1.5 text-sm focus:border-accent focus:outline-none"
            />
            <span className="text-xs text-mist">issued cert stays valid for</span>
          </label>
        </div>
      )}

      {phase === 'error' && (
        <div className="rounded-xl bg-rose-soft px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {phase === 'shown' && result && (
        <div className="space-y-5">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-canvas p-5">
            <canvas ref={qrCanvasRef} className="rounded-xl bg-white p-3" />
            <p className="text-xs text-mist">
              Expires {new Date(result.ticket.expiresAt).toLocaleString()}
            </p>
          </div>
          <p className="text-sm text-ink">
            Or enter manually on the terminal:
          </p>
          <ManualField label="Microservice URL" value={result.qrPayload.microserviceUrl} onCopy={copyToClipboard} />
          <ManualField label="Ticket ID"        value={result.qrPayload.ticketId}        onCopy={copyToClipboard} />
          <ManualField label="Ticket secret"    value={result.qrPayload.ticketSecret}    onCopy={copyToClipboard} mono />
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
            The secret is displayed once. Closing this dialog drops it from memory.
            If the terminal didn't capture it in time, mint a new ticket.
          </p>
        </div>
      )}
    </Modal>
  );
}

function ManualField({ label, value, onCopy, mono }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-36 shrink-0 text-xs uppercase tracking-wider text-mist">{label}</span>
      <code className={`flex-1 truncate rounded-lg bg-canvas px-3 py-1.5 text-xs ${mono ? 'font-mono' : ''} text-ink`}>
        {value}
      </code>
      <button
        type="button"
        onClick={() => onCopy(value, label)}
        title={`Copy ${label}`}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink"
      >
        <i className="fa-solid fa-copy" aria-hidden="true" />
      </button>
    </div>
  );
}
